"use client";

import { DEMO_CALENDAR_LINK } from "@/lib/constants/booking";
import Testimonial, { productionTestimonials } from "@/app/(public)/shared/components/Testimonial";
import { UnifiedModal, useModalManager } from "@/app/(public)/shared/components/UnifiedModal";
import { Button } from "@/app/(public)/shared/ui/Button";
import { CheckCircle } from "lucide-react";
import Link from "next/link";

const RegistrationGraphic = ({ isMobile = false }: { isMobile?: boolean }) => (
  <div
    className={
      isMobile
        ? "w-full max-w-sm mx-auto"
        : "absolute top-[50%] left-[50%] transform -translate-x-1/2 -translate-y-1/2 scale-[0.9] lg:scale-[1.1] origin-center"
    }
  >
    {/* Registration Form Mockup */}
    <div
      className={`${isMobile ? "w-full" : "w-[440px]"} bg-white/95 backdrop-blur-sm rounded-2xl p-6 sm:p-8 lg:p-10 shadow-2xl border border-gray-300`}
    >
      <div className="flex flex-col gap-6 sm:gap-8">
        <div className="flex flex-col gap-2 sm:gap-3">
          <div className="text-tag-md sm:text-tag-lg text-landing-primary">Event Registration</div>
          <div className="text-body-sm sm:text-body text-landing-secondary">Annual Company Retreat 2024</div>
        </div>

        <div className="flex flex-col gap-4 sm:gap-6">
          <div className="flex flex-col gap-2 sm:gap-3">
            <div className="text-tag-sm sm:text-tag-md text-landing-primary">Full Name</div>
            <div className="h-10 sm:h-12 rounded-lg px-3 sm:px-4 flex items-center border border-landing-light bg-gray-50">
              <div className="text-body-sm sm:text-body text-landing-secondary">John Smith</div>
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:gap-3">
            <div className="text-tag-sm sm:text-tag-md text-landing-primary">Email</div>
            <div className="h-10 sm:h-12 rounded-lg px-3 sm:px-4 flex items-center border border-landing-light bg-gray-50">
              <div className="text-body-sm sm:text-body text-landing-secondary">john@company.com</div>
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:gap-3">
            <div className="text-tag-sm sm:text-tag-md text-landing-primary">Arrival Date</div>
            <div className="h-10 sm:h-12 rounded-lg px-3 sm:px-4 flex items-center border border-landing-light bg-gray-50">
              <div className="text-body-sm sm:text-body text-landing-secondary">March 15, 2024</div>
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:gap-3">
            <div className="text-tag-sm sm:text-tag-md text-landing-primary">Dietary Restrictions</div>
            <div className="h-10 sm:h-12 rounded-lg px-3 sm:px-4 flex items-center border border-landing-light bg-gray-50">
              <div className="text-body-sm sm:text-body text-landing-secondary">Vegetarian</div>
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:gap-3">
            <div className="text-tag-sm sm:text-tag-md text-landing-primary">T-shirt Size</div>
            <div className="h-10 sm:h-12 rounded-lg px-3 sm:px-4 flex items-center border border-landing-light bg-gray-50">
              <div className="text-body-sm sm:text-body text-landing-secondary">Medium</div>
            </div>
          </div>
        </div>

        <div className="pt-2 sm:pt-4">
          <div className="w-full h-10 sm:h-12 bg-blue-600 rounded-lg flex items-center justify-center">
            <div className="text-white text-tag-sm sm:text-tag-md font-medium">Submit Registration</div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default function Registration() {
  const { openModal, closeModal, isModalOpen } = useModalManager();

  return (
    <main>
      {/* Hero Section */}
      <section className="min-h-[calc(100vh-64px)] py-8 sm:py-12 md:py-16 lg:py-8 mt-[64px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
            <div className="flex flex-col gap-8 sm:gap-12 lg:gap-24">
              <div className="flex flex-col gap-4 sm:gap-6">
                <h1 className="text-h2 sm:text-h1 text-landing-primary">Registration</h1>
                <p className="text-body sm:text-body-lg text-landing-secondary max-w-530">
                  Streamlined attendee registration and RSVP management.
                </p>
                <p className="text-tag-lg sm:text-tag-xl text-landing-primary max-w-530 font-semibold">
                  One form. Everything you need.
                </p>
                <p className="text-body-sm sm:text-body text-landing-secondary max-w-530">
                  Collecting event details from 50+ attendees? Don't rely on scattered Google Forms or messy
                  spreadsheets.
                </p>
                <p className="text-body-sm sm:text-body text-landing-secondary max-w-530">
                  Nowadays Registration lets you send a <strong>customizable form</strong> to your attendees—fully
                  integrated with the rest of your event planning workflow.
                </p>
                <div className="flex gap-3">
                  <Link href="/create">
                    <Button variant="primary" color="primary" size="md">
                      Try our platform
                    </Button>
                  </Link>
                  <Link href="/create">
                    <Button variant="outline" color="neutral" size="md">
                      Create an Event
                    </Button>
                  </Link>
                </div>
              </div>
            </div>

            {/* Mobile Graphic */}
            <div className="block md:hidden">
              <RegistrationGraphic isMobile={true} />
            </div>

            {/* Desktop Graphic */}
            <div className="hidden md:flex items-center justify-center relative bg-landing-secondary rounded-xl overflow-clip h-[500px] lg:h-[600px]">
              <RegistrationGraphic />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section - Document Style */}
      <section className="py-12 sm:py-16 md:py-20 lg:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="mb-16">
            <h2 className="text-h2 text-landing-primary mb-6">Registration forms that actually work</h2>
            <p className="text-body-lg text-landing-secondary max-w-2xl">
              Collect event details from 50+ attendees efficiently with fully customizable forms that integrate
              seamlessly with your event planning workflow.
            </p>
          </div>

          {/* Content */}
          <div className="space-y-12">
            <div>
              <h3 className="text-h3 text-landing-primary mb-4">Fully customizable questions for any event</h3>
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 mt-1">
                  <CheckCircle className="w-5 h-5 text-primary" />
                </div>
                <div className="space-y-2">
                  <h4 className="text-body-lg font-semibold text-landing-primary">
                    Fully customizable questions for any event. Flight preferences, dietary needs, t-shirt sizes,
                    emergency contacts - ask for exactly what you need.
                  </h4>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-h3 text-landing-primary mb-4">Real-time syncing with your event tools</h3>
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 mt-1">
                  <CheckCircle className="w-5 h-5 text-primary" />
                </div>
                <div className="space-y-2">
                  <h4 className="text-body-lg font-semibold text-landing-primary">
                    Attendee data automatically syncs with shuttle manifests, hotel assignments, flight bookings, and
                    agenda tools. Your team stays up to date without chasing responses or manually updating
                    spreadsheets. Everything flows seamlessly from registration to execution.
                  </h4>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-h3 text-landing-primary mb-4">Auto-reminders and follow-up management</h3>
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 mt-1">
                  <CheckCircle className="w-5 h-5 text-primary" />
                </div>
                <div className="space-y-2">
                  <h4 className="text-body-lg font-semibold text-landing-primary">
                    Never chase down missing registration details again. Automated reminder sequences ensure complete
                    responses while maintaining a professional, organized experience for your attendees. Set custom
                    reminder schedules that work for your timeline.
                  </h4>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-h3 text-landing-primary mb-4">One central dashboard for everything</h3>
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 mt-1">
                  <CheckCircle className="w-5 h-5 text-primary" />
                </div>
                <div className="space-y-2">
                  <h4 className="text-body-lg font-semibold text-landing-primary">
                    Whether you're booking airport pickups or ordering swag, your attendee data is already organized
                    exactly where it needs to be. No more scattered spreadsheets or lost responses. Access real-time
                    reporting and export capabilities that keep your entire team aligned.
                  </h4>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <Testimonial variant="wall" testimonials={productionTestimonials} title="Don't just take our word for it." />

      {/* Demo Modal */}
      <UnifiedModal
        type="cal"
        isOpen={isModalOpen("demo")}
        onClose={() => closeModal("demo")}
        title="Book a Demo"
        subtitle="Schedule a 30-minute demo with our team"
        size="lg"
        calLink={DEMO_CALENDAR_LINK}
        fallbackText="Book your demo with our team"
      />
    </main>
  );
}
